import { Button, Input, Text, TextTooltip } from '@/ui-kit'
import { Trans } from '@lingui/react/macro'
import { useWallet } from '@solana/wallet-adapter-react'
import { LAMPORTS_PER_SOL } from '@solana/web3.js'
import clsx from 'clsx'
import { Info } from 'lucide-react'
import { useFetchUserWallet } from '../../api'
import { useSolPrice } from '../../providers/SOLPriceProvider'

type StakeAmountInputProps = {
  value: string
  onChange: (value: string) => void
  balance: number
  isError?: boolean
  errorText?: string
}

const MAX_STAKE = 1_000_000
const MAX_DECIMALS = 8

export const StakeAmountInput = ({ value, onChange, balance, isError, errorText }: StakeAmountInputProps) => {
  const { publicKey } = useWallet()
  const { data: userWallet } = useFetchUserWallet()
  const { minimumRent } = useSolPrice()

  const handleChange = (raw: string) => {
    // Allow empty
    if (raw === '') {
      onChange('')
      return
    }

    // Only allow valid numeric input (digits, one dot, up to MAX_DECIMALS after dot)
    const regex = new RegExp(`^\\d*\\.?\\d{0,${MAX_DECIMALS}}$`)
    if (!regex.test(raw)) return

    const num = Number.parseFloat(raw)
    if (Number.isNaN(num)) return
    if (num > MAX_STAKE) return

    onChange(raw)
  }

  const handleMaxClick = () => {
    if (publicKey?.toString() !== userWallet?.wallet?.walletAddress) return
    onChange(Math.min(MAX_STAKE, Math.max(0, balance - 0.01)).toFixed(4))
  }

  return (
    <div className="">
      <div className="relative mt-1 mb-2 flex">
        <Input className="w-[68px] shrink-0" rounded="start" isDisabled value={'SOL'} />
        <Input
          className={clsx('-ml-px h-full flex-1')}
          inputClassName={clsx({ 'border-[color:var(--colorError)]!': isError })}
          rounded="end"
          value={value}
          onChange={(value) => handleChange(value)}
          placeholder={!value ? '0.0' : ''}
        />
        <div className="absolute top-2 right-2 z-1 flex gap-2">
          <Button
            isDisabled={!balance}
            size="small"
            className="hover:border-orange! border border-solid border-gray-100! bg-transparent! px-4! hover:bg-none!"
            onClick={handleMaxClick}
          >
            <Trans>Max</Trans>
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1 truncate">
          {errorText && (
            <Text className="colorError" type="body3-regular">
              {errorText}
            </Text>
          )}
        </div>
        <div className="text-gray-90 text-body-3 flex shrink-0 items-center gap-2">
          Rent: {(minimumRent / LAMPORTS_PER_SOL).toFixed(4)} SOL
          <TextTooltip
            className="flex max-w-[300px] items-center"
            side="bottom"
            text="A stake account requires a small amount of SOL (~0.002 SOL) to be locked as rent. This will be refunded when you close the stake account."
          >
            <Info className="h-3 w-3" />
          </TextTooltip>
        </div>
      </div>
    </div>
  )
}
